import { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { FloorMapProvider } from 'src/components/FloorMap/FloorMapProvider';
import { toFloor } from 'src/components/FloorSelect/FloorSelect';
import { Page } from 'src/components/Page/Page';
import { useMapStyle } from 'src/hooks/useMapStyle';
import { RoutePath } from 'src/routes/types';
import {
  getAdminFloorSelectItem,
  getAdminState,
} from 'src/store/admin/selectors';
import {
  getDeviceSize,
  getIsMobileWithTouchscreen,
} from 'src/store/agent/selectors';
import { navigationActions } from 'src/store/navigation/slice';
import { setSidesheetSlots } from 'src/store/sidesheet/slice';
import { SidesheetSlotItem } from 'src/store/sidesheet/types';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { DeviceSize } from 'src/types';

import { AdminSettingsBottomSheet } from './AdminSettingsBottomSheet/AdminSettingsBottomSheet';
import { AdministratorFloorMap } from './AdministratorFloorMap';
import classes from './AdministratorPage.module.scss';
import { AdministratorSettings } from './AdministratorSettings/AdministratorSettings';
import { AdminBottomSheet } from './AdministratorSidesheet/AdminBottomSheet';
import { PermissionsBottomSheet } from './AdministratorUserPermissions/PermissionsBottomSheet';
import { BuildingBottomSheet } from './BuildingBottomSheet/BuildingBottomSheet';
import { FloorBottomSheet } from './FloorBottomSheet/FloorBottomSheet';
import { TownBottomSheet } from './TownBottomSheet/TownBottomSheet';

const modalSidesheetSizes = new Set([
  DeviceSize.XXS,
  DeviceSize.XS,
  DeviceSize.S,
]);

export const AdministratorPage: FC = () => {
  const dispatch = useAppDispatch();
  const { backgroundData } = useSelector(getAdminState);
  const floor = useSelector(getAdminFloorSelectItem);
  const size = useSelector(getDeviceSize);
  const isMobile = useSelector(getIsMobileWithTouchscreen);

  useEffect(() => {
    const standardSlot: SidesheetSlotItem = {
      id: 'tabsLevel',
      mode: 'standard',
    };
    const modalSlot: SidesheetSlotItem = {
      id: 'tabsLevelModal',
      mode: 'modal',
    };
    const slot = modalSidesheetSizes.has(size) ? modalSlot : standardSlot;

    dispatch(
      setSidesheetSlots([
        {
          sidesheetId: 'adminBookingObject',
          slot,
        },
        {
          sidesheetId: 'adminTown',
          slot,
        },
        {
          sidesheetId: 'adminBuilding',
          slot,
        },
        {
          sidesheetId: 'adminOffice',
          slot,
        },
        {
          sidesheetId: 'adminFloor',
          slot,
        },
        {
          sidesheetId: 'adminFloor',
          slot,
        },
      ]),
    );
    dispatch(
      navigationActions.setSegments([RoutePath.Main, RoutePath.Administrator]),
    );
  }, [dispatch, size]);

  const [fixed, mapStyle] = useMapStyle();

  return (
    <Page
      actions={isMobile ? null : <AdministratorSettings />}
      fixed={fixed}
      testId='administrator'
      contentClassName={isMobile ? classes.mobile : undefined}
    >
      <div style={mapStyle}>
        <FloorMapProvider
          floor={toFloor(floor)}
          backgroundData={backgroundData}
        >
          <AdministratorFloorMap />
        </FloorMapProvider>
      </div>
      {isMobile && (
        <>
          <AdminSettingsBottomSheet />
          <TownBottomSheet />
          <BuildingBottomSheet />
          <FloorBottomSheet />
          <PermissionsBottomSheet />
          <AdminBottomSheet />
        </>
      )}
    </Page>
  );
};

export default AdministratorPage;
